import { useEffect, useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react'
import { Link, useFetcher, useLocation, useNavigate } from "@remix-run/react";
// import { User } from "#models/user.model";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useQuery } from 'convex/react';
import { api } from '#convex/_generated/api.js';
import { Doc, Id } from '#convex/_generated/dataModel.js';
import { inputStyle } from '#app/styles/styles.js';

function classNames(...classes: (string | boolean)[]) {
  return classes.filter(Boolean).join(' ')
}

type ViewAsSelectorProps = {
  subdivisionId: Id<"Subdivision">
  selectedUser: Doc<"User"> | Doc<"Subdivision"> | null
  setSelectedUser: (user: Doc<"User"> | Doc<"Subdivision"> | null) => void
  userId: Id<"User"> | null
}

export default function ViewAsSelector({ subdivisionId, selectedUser, setSelectedUser, userId }: ViewAsSelectorProps) {
  const [query, setQuery] = useState('')
  
  // const userFetcher = useFetcher();
  const location = useLocation();
  const users = useQuery(api.user.getUsersInSubdivision, { subdivisionId: subdivisionId  })
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedUser !== null) {
      navigate(getLink(selectedUser));
    }
  }, [selectedUser, navigate]);

  useEffect(() => {
    if(userId && selectedUser == null) {
      const user = users?.find((user: Doc<"User"> | null) => user?._id === userId)
      if(user) {
        setSelectedUser(user)
      }
    }
  }, [userId])

  if(users == null) {
    return (
      // <BarLoader color="#297582" />
      <p className="italic text-sm text-gray">Loading...</p>
    )
  }

  const filteredSubdivisionsAndUsers =
    query === '' ? [...users.filter((user) => user != null)] : [...users.filter((user) => user != null)].filter((item) => 
        item.firstName.toLowerCase().includes(query.toLowerCase()) || 
        item.lastName.toLowerCase().includes(query.toLowerCase()) ||
        item.email?.toLowerCase().includes(query.toLowerCase()) ||
        item.phoneNumber?.includes(query)
      )

  if(location.pathname.includes("dashboard/view/")) {
    const userId = location.pathname.split("/")[3] as Id<"User"> | Id<"Subdivision">;
    const user = filteredSubdivisionsAndUsers.find((user: Doc<"User"> | Doc<"Subdivision">) => user._id === userId);
    if(!user) {
      return <></>
    }
    return(
      <Link to={`/dashboard/view-as`} className="flex flex-row justify-between p-2 text-sm border-2 border-gray rounded-md">
        <p>{user.firstName + ' ' + user.lastName}</p>
        <XMarkIcon className="h-5 w-5" onClick={() => setSelectedUser(null)} />
      </Link>
    )
  }

  function getLink(user: Doc<"User"> | Doc<"Subdivision">) {
    if('subdivisions' in user && user.subdivisions && user.subdivisions.length > 0) {
      return `/dashboard/view/${user._id}/${user.subdivisions[0].toString()}/dashboard`
    } else {
      return `/dashboard/admin/${user._id}/dashboard`
    }
  }

  function getDisplayName(user: Doc<"User"> | Doc<"Subdivision">) {
    if(user == null) {
      return ''
    } else if('firstName' in user) {
      return user.firstName + ' ' + user.lastName
    } else {
      return user.name
    }
  }
  
  return (
    <div className="w-full text-left">
      <Combobox<Doc<"User"> | Doc<"Subdivision"> | null>  value={selectedUser} onChange={(user) => setSelectedUser(user)}>
        <div className="relative mt-2">
          <ComboboxInput
            className={inputStyle}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="View As"
            displayValue={(user: Doc<"User"> | Doc<"Subdivision">) => {
              return getDisplayName(user)
            }}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray" aria-hidden="true" />
          </ComboboxButton>
            <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredSubdivisionsAndUsers.map((user) => (
                  <ComboboxOption
                    key={user._id}
                    value={user}
                    className={({ focus }) =>
                      classNames(
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                        focus ? 'bg-blue text-white' : 'text-booBlack'
                      )
                    }
                  >
                    {({ focus, selected }) => (
                        <Link to={getLink(user)} prefetch="intent">
                          <span className={classNames('block truncate', selected && 'font-semibold')}>{getDisplayName(user)}</span>
                          {selected && (
                            <span
                              className={classNames(
                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                focus ? 'text-white' : 'text-blue'
                              )}
                            >
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </Link>
                        )}
                  </ComboboxOption>
                ))}
            </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  )
}
